import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import CreativeFileStatus from '@/Enums/Creatives/CreativeFileStatus.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { ICreative, ICreativeReduxState } from '@/Interfaces/Creatives/Creative.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DEFAULT_CREATIVE_FILTERS } from '@/constants/creativeLibrary';
import {
  CREATIVES_ARCHIVE_ENDPOINT,
  CREATIVES_BULK_ENDPOINT,
  CREATIVES_ENDPOINT,
  DV360_TICKETS_CREATIVES_ENDPOINT,
} from '@/constants/endpoints';
import { getAdvertisersFromClients } from '@/helpers/advertisers';
import { fetchClients } from '@/helpers/axios';
import { formatClientsResponseData } from '@/helpers/clients';
import {
  filterAdvertisersForCreativeLibrary,
  mapAdvertisersForCreativeLibrary,
  mapApiCreativesToTableData,
  transformCreativeFilters,
} from '@/helpers/creativeLibrary';
import { normalizeFileName } from '@/helpers/file';
import { addUnloadListener, removeUnloadListener } from '@/helpers/unloadListener';
import { setupVideoUploadWorker } from '@/helpers/worker';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import {
  BulkCreativeCreatePayload,
  CreativeArchivePayload,
  CreativeArchiveResult,
  CreativeCreatePayload,
  CreativeUpdatePayload,
} from '@/types/creativeLibrary';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { RootState } from '..';
import { getSignedUrl } from '../api/storage.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';

const initialState: ICreativeReduxState = {
  filters: DEFAULT_CREATIVE_FILTERS,
  advertisers: [],
  advertisersRequestId: null,
  creatives: [],
  total: 0,
  creativesRequestId: null,
};

export const creativeSlice = createSlice({
  name: 'creative',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAdvertisers.pending, pendingCaseHandler('advertisers'))
      .addCase(getAdvertisers.rejected, rejectedCaseHandler('advertisers'))
      .addCase(getAdvertisers.fulfilled, fulfilledCaseHandler('advertisers'))
      .addCase(getCreatives.pending, pendingCaseHandler('creatives'))
      .addCase(getCreatives.rejected, rejectedCaseHandler('creatives'))
      .addCase(getCreatives.fulfilled, (state, action) => {
        if (state.creativesRequestId === action.meta.requestId) {
          state.creatives = action.payload.data;
          state.total = action.payload.total;
          state.creativesRequestId = null;
        }
      })
      .addCase(updateCreative.fulfilled, (state, { payload }) => {
        state.creatives = state.creatives.map(creative =>
          creative.id === payload.id
            ? {
                ...payload,
                isInUse: creative.isInUse,
                advertiserName: payload.advertiser.name,
                clientName: payload.advertiser.client?.name ?? '',
              }
            : creative,
        );
      })
      .addCase(deleteCreative.fulfilled, (state, { meta }) => {
        state.creatives = state.creatives.filter(creative => creative.id !== meta.arg);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(archiveCreatives.fulfilled, (state, { payload: { updated } }) => {
        state.creatives = state.creatives.filter(creative => !updated.includes(creative.id));
        state.total = Math.max(0, state.total - updated.length);
      });
  },
});

export const { setFilters } = creativeSlice.actions;

export const getAdvertisers = createAsyncThunk<IControlOption[], void>('creativeSlice/getAdvertisers', async () => {
  const response = await fetchClients();

  const formattedAdvertisers = formatClientsResponseData(
    response,
    mapAdvertisersForCreativeLibrary,
    filterAdvertisersForCreativeLibrary,
  );

  return getAdvertisersFromClients(formattedAdvertisers as IClientControlOption[]).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
});

export const getCreatives = createAsyncThunk<{ data: ICreative[]; total: number }, { loadLineItems: boolean } | void>(
  'creativeSlice/getCreatives',
  async (args, { getState }) => {
    const state = getState() as RootState;
    const {
      creative: { filters },
    } = state;

    const { platform, ...otherFilters } = filters;

    const isDV360CampaignManagementEnabled = selectIsDV360CampaignManagementEnabled(state);
    const url =
      isDV360CampaignManagementEnabled && platform === CampaignPlatformType.Google
        ? DV360_TICKETS_CREATIVES_ENDPOINT
        : CREATIVES_ENDPOINT;

    const transformedFilters = transformCreativeFilters(otherFilters);
    const {
      data: {
        data,
        meta: { total },
      },
    } = await axios.get<IPaginatedApiResponse<ICreative[]>>(url, {
      params: {
        ...transformedFilters,
        ...(args?.loadLineItems && { loadLineItems: true }),
      },
    });
    return { data: mapApiCreativesToTableData(data), total };
  },
  {
    condition: (_, { getState }) => {
      const {
        creative: { creativesRequestId },
      } = getState() as RootState;

      return !creativesRequestId;
    },
  },
);

const handleUploadSuccess = async (creativeId: number, filePath: string) => {
  toast.success('Video file uploaded successfully');
  try {
    await axios.post(`${CREATIVES_ENDPOINT}/${creativeId}/process`, { filePath });
  } catch {
    toast.error('Failed to process video file');
  }
};

const handleUploadError = async (creativeId: number, error?: Error) => {
  toast.error('Failed to upload video file');
  console.error('Upload video file error', error);
  try {
    await axios.put(`${CREATIVES_ENDPOINT}/${creativeId}`, { fileStatus: CreativeFileStatus.FAILED });
  } catch {
    toast.error('Failed to update creative file status');
  }
};

const uploadVideoFile = async (file: File, creativeId: number) => {
  Sentry.captureMessage('Starting video upload', {
    extra: { creativeId, fileName: file.name, fileSize: file.size },
  });

  const { signedUrl, filePath } = await getSignedUrl(creativeId);

  Sentry.captureMessage('Signed URL obtained', {
    extra: { creativeId, filePath },
  });

  const normalizedFile = normalizeFileName(file);

  addUnloadListener();

  let videoUploadWorker: Worker;

  const cleanup = () => {
    removeUnloadListener();
    videoUploadWorker?.terminate();
  };

  const onMessage = async (
    event: MessageEvent<{
      success: boolean;
      error?: Error;
      errorType?: 'CORS_OR_NETWORK' | 'HTTP_ERROR' | 'UNKNOWN';
      httpStatus?: number;
      targetUrl?: string;
    }>,
  ) => {
    if (event.data.success) {
      Sentry.captureMessage('Worker reported upload success', {
        extra: { creativeId, filePath },
      });
      await handleUploadSuccess(creativeId, filePath);
    } else {
      const { error, errorType, httpStatus, targetUrl } = event.data;
      const isCors = errorType?.toLowerCase() === 'cors_or_network';
      Sentry.captureException(error ?? new Error('Video upload worker reported failure'), {
        extra: {
          creativeId,
          filePath,
          errorType: errorType?.toLowerCase() ?? 'unknown',
          ...(httpStatus !== undefined && { httpStatus }),
          ...(targetUrl && { targetUrlHost: new URL(targetUrl).hostname }),
          possibleCorsIssue: isCors,
        },
        tags: {
          upload_error_type: errorType?.toLowerCase() ?? 'unknown',
          ...(isCors && { cors_error: 'true' }),
        },
      });
      await handleUploadError(creativeId, error);
    }
    cleanup();
  };

  videoUploadWorker = setupVideoUploadWorker(signedUrl, normalizedFile, onMessage);

  toast.info(
    'Uploading video file in background. Please do not refresh or close the page until the upload is complete.',
  );
};

export const createCreative = createAsyncThunk<ICreative, CreativeCreatePayload, IRejectValueThunkConfig>(
  'creativeSlice/createCreative',
  async (payload, { rejectWithValue }) => {
    try {
      const isVideoType = payload.type === CreativeType.VIDEO;
      const { file, ...restPayload } = payload;

      const { data } = await axios.postForm<ICreative>(CREATIVES_ENDPOINT, isVideoType ? restPayload : payload);

      if (isVideoType && file) {
        await uploadVideoFile(file, data.id);
      }

      return data;
    } catch (err) {
      // Preserved as-is (not re-thrown) so the caller's .unwrap() gets the original
      // AxiosError, including response.data.message — createAsyncThunk's default
      // rejection path serializes that away, dropping the API's actual error text.
      return rejectWithValue(err as Error);
    }
  },
);

export const updateCreative = createAsyncThunk<ICreative, CreativeUpdatePayload, IRejectValueThunkConfig>(
  'creativeSlice/updateCreative',
  async (payload, { rejectWithValue }) => {
    try {
      const { id, file, ...restPayload } = payload;
      const isVideoType = payload.type === CreativeType.VIDEO;

      const { data } = await axios.putForm<ICreative>(
        `${CREATIVES_ENDPOINT}/${id}`,
        isVideoType ? restPayload : payload,
      );

      if (isVideoType && file) {
        await uploadVideoFile(file, data.id);
      }

      return data;
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export const deleteCreative = createAsyncThunk<void, number>('creativeSlice/deleteCreative', async id => {
  await axios.delete(`${CREATIVES_ENDPOINT}/${id}`);
});

export const archiveCreatives = createAsyncThunk<CreativeArchiveResult, CreativeArchivePayload>(
  'creativeSlice/archiveCreatives',
  async payload => {
    const { data } = await axios.post<CreativeArchiveResult>(CREATIVES_ARCHIVE_ENDPOINT, payload);
    return data;
  },
);

const uploadBulkZipFile = async (file: File, restPayload: Omit<BulkCreativeCreatePayload, 'file'>) => {
  const { signedUrl, filePath } = await getSignedUrl();

  addUnloadListener();

  let uploadWorker: Worker;

  const cleanup = () => {
    removeUnloadListener();
    uploadWorker?.terminate();
  };

  const onMessage = async (event: MessageEvent<{ success: boolean; error?: Error }>) => {
    if (event.data.success) {
      try {
        await axios.post(CREATIVES_BULK_ENDPOINT, { ...restPayload, filePath });
        toast.success('Bulk upload completed. Creatives will appear after processing.');
      } catch {
        toast.error('Failed to start bulk creative processing');
      }
    } else {
      toast.error('Failed to upload zip file');
      console.error('Upload zip file error', event.data.error);
    }
    cleanup();
  };

  uploadWorker = setupVideoUploadWorker(signedUrl, file, onMessage);

  toast.info('Uploading zip file in background. Please do not refresh or close the page until the upload is complete.');
};

export const bulkCreateCreatives = createAsyncThunk<void, BulkCreativeCreatePayload>(
  'creativeSlice/bulkCreateCreatives',
  async payload => {
    const { file, ...restPayload } = payload;
    await uploadBulkZipFile(file, restPayload);
  },
);

export default creativeSlice.reducer;
